import { Box, HStack, Text, Button } from '@chakra-ui/react';
import { FiNavigation, FiDownload } from 'react-icons/fi'
import type { TurfDto } from '../../types/turf'
import { TURF_COLORS } from '../../lib/mapConfig'
import { ResponsiveTable, type ResponsiveColumn } from '../shared'

interface TurfMapTurfTableProps {
  turfs: TurfDto[]
  turfHouseholdCounts: Record<string, number>
  onTurfClick: (turf: TurfDto) => void
  onOptimize: (turfId: string) => void
  onExport: (turfId: string, label: string) => void
  isOptimizing: boolean
  isExporting: boolean
}

export function TurfMapTurfTable({
  turfs,
  turfHouseholdCounts,
  onTurfClick,
  onOptimize,
  onExport,
  isOptimizing,
  isExporting,
}: TurfMapTurfTableProps) {
  const columns: ResponsiveColumn<TurfDto>[] = [
    {
      header: '#',
      mobileRole: 'meta',
      width: '60px',
      cell: (_turf, idx) => {
        return (
          <HStack gap={2}>
            <Box
              w="12px"
              h="12px"
              borderRadius="sm"
              bg={TURF_COLORS[idx % TURF_COLORS.length]}
            />
            <Text fontWeight="medium" color="gray.500">
              {idx + 1}
            </Text>
          </HStack>
        );
      },
    },
    {
      header: 'Label',
      mobileRole: 'title',
      cell: (turf) => <Text fontWeight="medium">{turf.label}</Text>,
    },
    {
      header: 'Contacts',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (turf) => <Text fontSize="sm">{turf.memberCount}</Text>,
    },
    {
      header: 'Households',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (turf) => <Text fontSize="sm">{turfHouseholdCounts[turf.id] ?? 0}</Text>,
    },
    {
      header: 'Assigned To',
      mobileRole: 'meta',
      cell: (turf) => <Text fontSize="sm">{turf.assignedToName ?? '-'}</Text>,
    },
    {
      header: 'Progress',
      mobileRole: 'meta',
      cell: (turf) => {
        const turfProgressPct =
          turf.memberCount > 0
            ? Math.round((turf.visitedCount / turf.memberCount) * 100)
            : 0
        return (
          <Text fontSize="sm">
            {turf.visitedCount}/{turf.memberCount}
            <Text as="span" color="gray.400" ml={1}>
              ({turfProgressPct}%)
            </Text>
          </Text>
        )
      },
    },
    {
      header: 'Actions',
      mobileRole: 'meta',
      cell: (turf) => (
        <HStack gap={1} onClick={(e) => e.stopPropagation()}>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onOptimize(turf.id)}
            loading={isOptimizing}><FiNavigation />Optimize
                      </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onExport(turf.id, turf.label)}
            loading={isExporting}><FiDownload />Export
                      </Button>
        </HStack>
      ),
    },
  ]

  return (
    <Box bg="surface" borderRadius="lg" shadow="md" overflow="hidden">
      <Box p={4} borderBottom="1px" borderColor="gray.100">
        <Text fontWeight="medium">Turfs ({turfs.length})</Text>
      </Box>
      {turfs.length === 0 ? (
        <Box p={8} textAlign="center">
          <Text color="gray.500">No turfs in this map.</Text>
        </Box>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={turfs}
          keyFn={(turf) => turf.id}
          onRowClick={onTurfClick}
        />
      )}
    </Box>
  )
}
